import React, { useMemo } from "react";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Button, Stack } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import type { PropertyMappingProps } from "./PropertyMapping.types";
import { usePropertyMappingViewModel } from "./PropertyMapping.vm";
import GlobalPropertyMappingSelector from "../../../../GeoData/GlobalPropertyMappingSelector";
import PropertyMappingSelector from "../../../../GeoData/PropertyMappingSelector";
import type { PropertyMappings } from "../../../../GeoData/PropertyMappingSelector/PropertyMappingSelector.types";
import "./PropertyMapping.css";

/** Extract file names from file paths (last segment without extension) */
function getFileNames(filePaths: string[]): string {
	return filePaths
		.map((path) => {
			const lastSegment = path.split("/").pop() || path;
			// Remove common extensions
			return lastSegment.replace(/\.(shp|dbf|shx|prj|cpg|json|geojson|zip|kml|kmz)$/i, "");
		})
		.join(", ");
}

/** Extract directory path from file paths (everything before the last segment) */
function getDirectoryPath(filePaths: string[]): string | null {
	if (filePaths.length === 0) return null;
	const firstPath = filePaths[0];
	const lastSlashIndex = firstPath.lastIndexOf("/");
	if (lastSlashIndex === -1) return null;
	return firstPath.substring(0, lastSlashIndex);
}

/**
 * PropertyMapping step component
 *
 * Allows users to configure which file properties map to which job fields.
 * Supports global "Apply to All" mappings and per-group customization.
 */
export default function PropertyMapping(props: PropertyMappingProps) {
	const { geodataGroups, propertyMappings, company } = props;
	const vm = usePropertyMappingViewModel(props);

	// Memoize callbacks for each group to maintain stable references for React.memo
	// This prevents PropertyMappingSelector from re-rendering when parent re-renders
	const groupCallbacks = useMemo(() => {
		const callbacks: Record<string, (mappings: PropertyMappings) => void> = {};
		for (const group of geodataGroups) {
			callbacks[group.id] = (mappings: PropertyMappings) => vm.handleGroupMappingChange(group.id, mappings);
		}
		return callbacks;
	}, [geodataGroups, vm.handleGroupMappingChange]);

	return (
		<Box className="property-mapping" sx={{ p: 2 }}>
			{/* Global Property Mapping Selector */}
			<Box sx={{ mb: 3 }}>
				<GlobalPropertyMappingSelector
					onApplyMapping={vm.handleGlobalMappingApply}
					availableProperties={vm.allAvailableProperties}
					autoGuessedMappings={vm.aggregatedAutoGuesses}
					company={company}
				/>
			</Box>

			{/* Per-Group Section Header */}
			<Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="subtitle1">
					Per-Group Mappings ({vm.totalGroups} {vm.totalGroups === 1 ? "group" : "groups"})
				</Typography>
				<Stack direction="row" spacing={1}>
					<Button
						size="small"
						startIcon={<ExpandMore sx={{ transform: "rotate(180deg)" }} />}
						onClick={vm.handleExpandAll}
						disabled={vm.expandedGroups.size === geodataGroups.length}
					>
						Expand All
					</Button>
					<Button
						size="small"
						startIcon={<ExpandLess sx={{ transform: "rotate(180deg)" }} />}
						onClick={vm.handleCollapseAll}
						disabled={vm.expandedGroups.size === 0}
					>
						Collapse All
					</Button>
				</Stack>
			</Box>

			{/* Per-Group Accordions */}
			<Box sx={{ overflow: "hidden" }}>
				{geodataGroups.map((group) => {
					const isExpanded = vm.expandedGroups.has(group.id);
					const groupMappings = propertyMappings[group.id];

					return (
						<Accordion
							key={group.id}
							expanded={isExpanded}
							onChange={() => vm.handleToggleGroup(group.id)}
							sx={{ mb: 1, overflow: "hidden" }}
						>
							<AccordionSummary
								expandIcon={<ExpandMore />}
								sx={{ overflow: "hidden", "& .MuiAccordionSummary-content": { overflow: "hidden", minWidth: 0 } }}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", overflow: "hidden" }}>
									{/* Group name - show file names with directory path */}
									<Box sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
										<Typography variant="subtitle2" noWrap>
											{getFileNames(group.filePaths)}
										</Typography>
										{getDirectoryPath(group.filePaths) && (
											<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
												{getDirectoryPath(group.filePaths)}
											</Typography>
										)}
									</Box>

									{/* Feature count chip */}
									<Chip
										label={`${group.featureCount} ${group.geometryType === "polygon" ? "polygons" : "points"}`}
										size="small"
										variant="outlined"
									/>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								{/* Only render PropertyMappingSelector when expanded to avoid mounting all N components at once */}
								{isExpanded && groupMappings && (
									<PropertyMappingSelector
										geoJsons={[group.geoJSON]}
										propertyMappings={groupMappings}
										onMappingChange={groupCallbacks[group.id]}
										company={company}
									/>
								)}
								{isExpanded && !groupMappings && (
									<Typography variant="body2" color="text.secondary">
										No property mappings configured for this group.
									</Typography>
								)}
							</AccordionDetails>
						</Accordion>
					);
				})}
			</Box>

			{/* Empty state */}
			{geodataGroups.length === 0 && (
				<Box sx={{ textAlign: "center", py: 4 }}>
					<Typography variant="body1" color="text.secondary">
						No geodata groups available for property mapping.
					</Typography>
				</Box>
			)}
		</Box>
	);
}
